import { useEffect, useState } from "react";
import { Monitor, Wrench, Printer, Network, Cctv, BatteryCharging, Building2, PlayCircle } from "lucide-react";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { PageHero, SectionHead, CardsGrid, CtaBand, splitRows, CARD_MAX_W } from "@/components/blocks";
import { ArticleCard } from "@/components/ArticleCard";
import { categorySlug } from "@/data/techAdvice";
import { breadcrumbJsonLd } from "@/lib/seo";

const CATEGORY_ICONS = {
  "Computers & PCs": Monitor,
  "PC Care & Troubleshooting": Wrench,
  "Printers, Toner & Ink": Printer,
  "Networking": Network,
  "CCTV": Cctv,
  "UPS & Power": BatteryCharging,
  "Small Business Technology": Building2,
  "Watch & Learn": PlayCircle,
};

export default function TechAdvice() {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/articles/categories"), api.get("/articles", { params: { limit: 6 } })])
      .then(([catRes, artRes]) => {
        setCategories(catRes.data);
        setArticles(artRes.data.items);
      })
      .finally(() => setLoading(false));
  }, []);

  const [mainArticles, lastArticleRow] = splitRows(articles, 3);

  return (
    <>
      <Seo
        title="Technology Advice | Cherians Tech Solutions"
        description="Practical technology guidance from Cherians Tech Solutions to help you understand, choose, maintain and use technology more effectively."
        jsonLd={breadcrumbJsonLd([{ label: "Technology Advice" }])}
      />
      <PageHero
        eyebrow="TECHNOLOGY ADVICE"
        title="Technology Advice"
        intro="CTS shares practical technology guidance to help individuals, businesses and institutions understand, choose, maintain and use technology more effectively."
        crumbs={[{ label: "Technology Advice" }]}
      />

      <section className="section-pad" data-testid="tech-advice-categories">
        <div className="container-site">
          <SectionHead eyebrow="BROWSE BY TOPIC" title="Browse by Category" />
          <CardsGrid
            cols={4}
            items={categories
              .filter((c) => c.name !== "Watch & Learn")
              .map((c) => ({
                icon: CATEGORY_ICONS[c.name],
                title: c.name,
                text: `${c.count} article${c.count === 1 ? "" : "s"}`,
                to: `/technology-advice/category/${categorySlug(c.name)}`,
              }))}
          />
        </div>
      </section>

      <section className="section-pad bg-cts-soft" data-testid="tech-advice-recent">
        <div className="container-site">
          <SectionHead eyebrow="LATEST GUIDANCE" title="Recent Articles" />
          {loading ? null : articles.length === 0 ? (
            <div className="card-base p-10 text-center max-w-2xl" data-testid="tech-advice-empty-state">
              <p className="text-[15px] leading-relaxed text-cts-charcoal">
                Our Technology Advice articles are being prepared. Check back soon for practical guidance on
                computers, printers, networking, CCTV and more.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {mainArticles.map((a, i) => <ArticleCard key={a.slug} article={a} i={i} />)}
              </div>
              {lastArticleRow.length > 0 && (
                <div className="flex flex-wrap justify-center gap-6 mt-6">
                  {lastArticleRow.map((a, i) => (
                    <div key={a.slug} className="w-full sm:w-[calc(50%-12px)]" style={{ maxWidth: CARD_MAX_W[3] }}>
                      <ArticleCard article={a} i={mainArticles.length + i} />
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
